// src/stores/appSettings.ts
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { gql } from '@apollo/client/core';
import { apolloClient } from '@/lib/apollo';
import { useStorage } from '@/composables/useStorage';

export type PostKind = 'feed' | 'listing';

export type Constraints = {
  plot: { nameMax: number; descriptionMax: number };
  feed: { titleMax: number; bodyMax: number };
  listing: { titleMax: number; bodyMax: number };
  message: { bodyMax: number };
};

type AppSettingsPayload = {
  appSettings: {
    version: string;
    constraints: Constraints;
  };
};

const STORAGE_KEY = 'app_settings_v1';

const DEFAULT_CONSTRAINTS: Constraints = {
  plot: { nameMax: 30, descriptionMax: 150 },
  feed: { titleMax: 255, bodyMax: 600 },
  listing: { titleMax: 30, bodyMax: 150 },
  message: { bodyMax: 1000 },
};

const APP_SETTINGS_QUERY = gql`
  query AppSettings {
    appSettings {
      version
      constraints {
        plot { nameMax descriptionMax }
        feed { titleMax bodyMax }
        listing { titleMax bodyMax }
        message { bodyMax }
      }
    }
  }
`;

export const useAppSettingsStore = defineStore('appSettings', () => {
  const storage = useStorage();

  const constraints = ref<Constraints>(DEFAULT_CONSTRAINTS);
  const version = ref<string>('');
  const loaded = ref(false);
  const loading = ref(false);
  const lastError = ref<string | null>(null);

  const isReady = computed(() => loaded.value);

  function postTitleMax(kind: PostKind) {
    return constraints.value[kind].titleMax;
  }

  function postBodyMax(kind: PostKind) {
    return constraints.value[kind].bodyMax;
  }

  const plotNameMax = computed(() => constraints.value.plot.nameMax);
  const plotDescriptionMax = computed(() => constraints.value.plot.descriptionMax);
  const messageBodyMax = computed(() => constraints.value.message.bodyMax);

  async function hydrateFromStorage() {
    const cached = await storage.get<{ version: string; constraints: Constraints } | null>(
      STORAGE_KEY,
      null
    );

    if (cached?.constraints) {
      constraints.value = cached.constraints;
      version.value = cached.version ?? '';
    }

    loaded.value = true;
  }

  async function persistToStorage() {
    await storage.set(STORAGE_KEY, {
      version: version.value,
      constraints: constraints.value,
    });
  }

  async function refreshFromApi() {
    if (loading.value) return;
    loading.value = true;
    lastError.value = null;

    try {
      const { data } = await apolloClient.query<AppSettingsPayload>({
        query: APP_SETTINGS_QUERY,
        fetchPolicy: 'network-only',
      });

      const remote = data?.appSettings;
      if (!remote?.constraints) return;

      if (remote.version !== version.value) {
        constraints.value = remote.constraints;
        version.value = remote.version ?? '';
        await persistToStorage();
      }
    } catch (e: any) {
      lastError.value = e?.message ?? 'Failed to refresh app settings.';
      // don’t throw — cached settings should keep the app working
      console.warn('[appSettings] refresh failed:', e);
    } finally {
      loading.value = false;
    }
  }

  /**
   * Call this on app start (after auth restore) and after login.
   * It is safe to call multiple times.
   */
  async function init() {
    if (!loaded.value) await hydrateFromStorage();
    // refresh in the background (awaiting is fine too; your call)
    await refreshFromApi();
  }

  return {
    constraints,
    version,
    loaded,
    loading,
    lastError,
    isReady,
    postTitleMax,
    postBodyMax,
    plotNameMax,
    plotDescriptionMax,
    messageBodyMax,
    hydrateFromStorage,
    refreshFromApi,
    init,
  };
});